<template>
	<div class="text-ternary checkoutPriceBreakdownComponent">
		<div class="flex justify-between">
			<p>{{ $t("subtotal") }}</p>
			<p class="font-serif">{{ currency.symbol }}{{ parseFloat(subtotal).toFixed(2) }}</p>
		</div>

		<div
			v-if="refundableBookingCost"
			class="flex justify-between">
			<p>{{ $t("refundableBooking") }}</p>
			<p class="font-serif">{{ currency.symbol }}{{ parseFloat(refundableBookingCost).toFixed(2) }}</p>
		</div>

		<div
			v-if="discountedAmount"
			class="flex justify-between items-center gap-3">
			<div class="flex items-center gap-2">
				<p>{{ discountLabel }}</p>
				<button
					v-if="hasAppliedPromocode"
					type="button"
					class="flex h-5 w-5 items-center justify-center rounded-full text-sm text-card-checkout-left-border transition hover:bg-secondary/10 hover:text-card-checkout-left-border"
					:aria-label="$t('removeCoupon', { code: appliedPromocode.promocode })"
					@click="removePromoCode">
					<IconsFontAwesome icon="fa-solid fa-trash" />
				</button>
			</div>
			<p class="font-serif text-primary">-{{ currency.symbol }}{{ parseFloat(discountedAmount).toFixed(2) }}</p>
		</div>

		<div
			v-for="[key, value] in Object.entries(fees)"
			:key="key"
			class="flex justify-between">
			<p>{{ key }}</p>
			<p class="font-serif">{{ currency.symbol }}{{ parseFloat(value).toFixed(2) }}</p>
		</div>

		<div class="border-t border-1 my-2"></div>

		<div class="flex justify-between text-primary">
			<p>{{ $t("total") }}</p>
			<p class="font-serif">{{ currency.symbol }}{{ parseFloat(total).toFixed(2) }}</p>
		</div>

		<div
			v-if="visibleCredits.length"
			class="border-t border-1 my-2"></div>

		<div
			v-for="credit in visibleCredits"
			:key="`${credit.code}-${credit.applied_amount}`"
			class="flex justify-between items-center gap-3">
			<div class="flex items-center gap-2">
				<p>{{ `${$t("credits")} (${credit.code})` }}</p>
				<button
					type="button"
					class="flex h-5 w-5 items-center justify-center rounded-full text-sm text-card-checkout-left-border transition hover:bg-secondary/10 hover:text-card-checkout-left-border"
					:aria-label="$t('removeCredit', { code: credit.code })"
					@click="removeCreditCode(credit.code)">
					<IconsFontAwesome icon="fa-solid fa-trash" />
				</button>
			</div>
			<p class="font-serif text-primary">
				-{{ currency.symbol }}{{ parseFloat(credit.applied_amount || 0).toFixed(2) }}
			</p>
		</div>
	</div>
</template>

<script setup>
	import { useNuxtApp } from "#app"
	import { useI18n } from "vue-i18n"
	import { useCurrencyStore } from "~/stores/CurrencyStore"
	import { storeToRefs } from "pinia"
	import { useCartStore } from "~/stores/CartStore"

	const { $cartService } = useNuxtApp()
	const { t } = useI18n()
	const currencyStore = useCurrencyStore()
	const { currency } = storeToRefs(currencyStore)

	const cartStore = useCartStore()
	const { subtotal, fees, total, refundableBookingCost, discountedAmount, appliedPromocode, appliedCredits } =
		storeToRefs(cartStore)
	const hasAppliedPromocode = computed(() => {
		return appliedPromocode.value ? !!appliedPromocode.value.promocode : false
	})
	const discountLabel = computed(() => {
		if (hasAppliedPromocode.value) {
			return `${t("discounts")} (${appliedPromocode.value.promocode})`
		}
		return t("discounts")
	})
	const removePromoCode = () => {
		$cartService.$removePromoCode()
	}
	const visibleCredits = computed(() => {
		return (appliedCredits.value || []).filter((credit) => Number(credit.applied_amount || 0) > 0)
	})
	const removeCreditCode = (code) => {
		$cartService.$removeCreditCode(code)
	}
</script>

<style scoped></style>
